"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowTopRightOnSquareIcon, DocumentDuplicateIcon, EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type TouchEvent } from "react";
import CommonDropdownItemsDefaultWrapper from "@/components/utils/select/dropdown/CommonDropdownItemsDefaultWrapper";
import useHasTouchInput from "@/hooks/useHasTouchInput";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { useLinkPreviewContext } from "./LinkPreviewContext";
type StopEvent = MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement> | PointerEvent<HTMLElement> | TouchEvent<HTMLElement>;
type ChatItemHrefButtonsLayout = "overlay" | "rail";
const stopPropagation = (event: StopEvent) => {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation();
};
const OVERLAY_TRIGGER_BUTTON_CLASSES = "tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-black/70 tw-text-iron-100 tw-shadow-lg tw-shadow-black/40 tw-backdrop-blur-sm tw-transition tw-duration-200 hover:tw-bg-black/85 hover:tw-text-white focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400";
const MENU_ITEM_CLASSES = "tw-flex tw-w-full tw-items-center tw-gap-x-2 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-text-iron-100 tw-transition-colors hover:tw-bg-iron-800 focus-visible:tw-outline-none focus-visible:tw-bg-iron-800";
const RAIL_BUTTON_CLASSES = "tw-flex tw-items-center tw-gap-x-2 tw-rounded-xl tw-border-0 tw-bg-iron-900 tw-p-2 hover:tw-text-iron-400";
function PreviewToggleIcon(t0) {
  const $ = _c(2);
  const {
    isLoading
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <svg className="tw-h-4 tw-w-4 tw-animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <svg className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-transition tw-duration-300 tw-ease-out" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 6.75C3 5.7835 3.7835 5 4.75 5H19.25C20.2165 5 21 5.7835 21 6.75V17.25C21 18.2165 20.2165 19 19.25 19H4.75C3.7835 19 3 18.2165 3 17.25V6.75Z" stroke="currentColor" strokeWidth="1.5" /><path d="M6.25 8.5H17.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><path d="M6.25 12.25H11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><path d="M6.25 15.5H11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><path d="M14 12H18V16H14V12Z" stroke="currentColor" strokeWidth="1.5" /><path d="M4 4L20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ExternalLinkIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="tw-h-4 tw-w-4 tw-flex-shrink-0" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" strokeWidth="3" stroke="currentColor" fill="none" aria-hidden="true"><path d="M55.4,32V53.58a1.81,1.81,0,0,1-1.82,1.82H10.42A1.81,1.81,0,0,1,8.6,53.58V10.42A1.81,1.81,0,0,1,10.42,8.6H32" /><polyline points="40.32 8.6 55.4 8.6 55.4 24.18" /><line x1="19.32" y1="45.72" x2="54.61" y2="8.91" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export default function ChatItemHrefButtons(t0) {
  const $ = _c(104);
  const {
    href,
    relativeHref,
    hideLink: t1,
    layout: t2
  } = t0;
  const hideLink = t1 === undefined ? false : t1;
  const layout = t2 === undefined ? "rail" : t2;
  const {
    previewToggle,
    onCardActionsActiveChange
  } = useLinkPreviewContext();
  const hasTouchInput = useHasTouchInput();
  const isMobileDevice = useIsMobileDevice();
  const [copied, setCopied] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const actionSurfaceId = useId();
  const buttonRef = useRef(null);
  const previewToggleButtonRef = useRef(null);
  const copyButtonRef = useRef(null);
  const openLinkButtonRef = useRef(null);
  const lastReportedActiveRef = useRef(false);
  const shouldFocusFirstMenuItemRef = useRef(false);
  const shouldIgnoreNextPointerFocusRef = useRef(false);
  const shouldRestoreTriggerFocusRef = useRef(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      isMenuOpen: false,
      isHovered: false,
      isFocused: false
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const actionSurfaceStateRef = useRef(t3);
  const showPreviewToggle = Boolean(previewToggle && !previewToggle.isHidden);
  const isOverlay = layout === "overlay";
  const showPersistentOverlayTrigger = isOverlay && (hasTouchInput || isMobileDevice);
  const isPreviewToggleDisabled = Boolean(previewToggle && (previewToggle.isLoading || !previewToggle.canToggle));
  let t4;
  let t5;
  if ($[1] !== actionSurfaceId || $[2] !== onCardActionsActiveChange) {
    t4 = () => () => {
      if (lastReportedActiveRef.current && onCardActionsActiveChange) {
        onCardActionsActiveChange(actionSurfaceId, false);
        lastReportedActiveRef.current = false;
      }
    };
    t5 = [actionSurfaceId, onCardActionsActiveChange];
    $[1] = actionSurfaceId;
    $[2] = onCardActionsActiveChange;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] !== isMenuOpen) {
    t6 = () => {
      if (isMenuOpen || !shouldRestoreTriggerFocusRef.current) {
        return;
      }
      const timeoutId = globalThis.window.setTimeout(() => {
        shouldRestoreTriggerFocusRef.current = false;
        buttonRef.current?.focus();
      }, 0);
      return () => {
        globalThis.window.clearTimeout(timeoutId);
      };
    };
    t7 = [isMenuOpen];
    $[5] = isMenuOpen;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[8] !== isMenuOpen || $[9] !== isOverlay) {
    t8 = () => {
      if (!isOverlay || !isMenuOpen) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key !== "Escape") {
          return;
        }
        shouldRestoreTriggerFocusRef.current = true;
      };
      globalThis.window.addEventListener("keydown", handleKeyDown, true);
      return () => {
        globalThis.window.removeEventListener("keydown", handleKeyDown, true);
      };
    };
    t9 = [isMenuOpen, isOverlay];
    $[8] = isMenuOpen;
    $[9] = isOverlay;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] !== actionSurfaceId || $[13] !== isOverlay || $[14] !== onCardActionsActiveChange) {
    t10 = nextStatePatch => {
      const currentState = actionSurfaceStateRef.current;
      const nextState = {
        ...currentState,
        ...nextStatePatch
      };
      if (nextState.isMenuOpen === currentState.isMenuOpen && nextState.isHovered === currentState.isHovered && nextState.isFocused === currentState.isFocused) {
        return;
      }
      actionSurfaceStateRef.current = nextState;
      if (nextState.isMenuOpen !== currentState.isMenuOpen) {
        setIsMenuOpen(nextState.isMenuOpen);
      }
      if (!isOverlay || !onCardActionsActiveChange) {
        return;
      }
      const isActionSurfaceActive = nextState.isMenuOpen || nextState.isHovered || nextState.isFocused;
      if (lastReportedActiveRef.current === isActionSurfaceActive) {
        return;
      }
      onCardActionsActiveChange(actionSurfaceId, isActionSurfaceActive);
      lastReportedActiveRef.current = isActionSurfaceActive;
    };
    $[12] = actionSurfaceId;
    $[13] = isOverlay;
    $[14] = onCardActionsActiveChange;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const updateActionSurfaceState = t10;
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      if (!shouldFocusFirstMenuItemRef.current) {
        return;
      }
      const firstEnabledMenuItem = [previewToggleButtonRef.current, copyButtonRef.current, openLinkButtonRef.current].find(_temp);
      if (!firstEnabledMenuItem) {
        return;
      }
      firstEnabledMenuItem.focus();
      shouldFocusFirstMenuItemRef.current = false;
    };
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const focusFirstMenuItemIfRequested = t11;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = element_0 => {
      previewToggleButtonRef.current = element_0;
      focusFirstMenuItemIfRequested();
    };
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const handlePreviewToggleButtonRef = t12;
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = element_1 => {
      copyButtonRef.current = element_1;
      focusFirstMenuItemIfRequested();
    };
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const handleCopyButtonRef = t13;
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = element_2 => {
      openLinkButtonRef.current = element_2;
      focusFirstMenuItemIfRequested();
    };
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const handleOpenLinkButtonRef = t14;
  let t15;
  if ($[20] !== updateActionSurfaceState) {
    t15 = t16 => {
      const {
        restoreFocusToTrigger: t17
      } = t16 === undefined ? {} : t16;
      const restoreFocusToTrigger = t17 === undefined ? false : t17;
      shouldFocusFirstMenuItemRef.current = false;
      shouldRestoreTriggerFocusRef.current = restoreFocusToTrigger;
      updateActionSurfaceState({
        isMenuOpen: false
      });
    };
    $[20] = updateActionSurfaceState;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const closeMenu = t15;
  let t16;
  if ($[22] !== updateActionSurfaceState) {
    t16 = nextIsOpen => {
      if (!nextIsOpen) {
        shouldFocusFirstMenuItemRef.current = false;
      }
      updateActionSurfaceState({
        isMenuOpen: nextIsOpen
      });
    };
    $[22] = updateActionSurfaceState;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const handleMenuOpenChange = t16;
  let t17;
  if ($[24] !== closeMenu || $[25] !== isPreviewToggleDisabled || $[26] !== previewToggle) {
    t17 = event_0 => {
      stopPropagation(event_0);
      if (!previewToggle || isPreviewToggleDisabled) {
        return;
      }
      previewToggle.onToggle();
      closeMenu({
        restoreFocusToTrigger: event_0.detail === 0
      });
    };
    $[24] = closeMenu;
    $[25] = isPreviewToggleDisabled;
    $[26] = previewToggle;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const toggleLinkPreviews = t17;
  let t18;
  if ($[28] !== closeMenu || $[29] !== href) {
    t18 = event_1 => {
      stopPropagation(event_1);
      closeMenu({
        restoreFocusToTrigger: event_1.detail === 0
      });
      navigator.clipboard.writeText(href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 500);
      });
    };
    $[28] = closeMenu;
    $[29] = href;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const copyToClipboard = t18;
  let t19;
  if ($[31] !== updateActionSurfaceState) {
    t19 = event_2 => {
      stopPropagation(event_2);
      shouldIgnoreNextPointerFocusRef.current = false;
      const nextIsMenuOpen = !actionSurfaceStateRef.current.isMenuOpen;
      shouldFocusFirstMenuItemRef.current = nextIsMenuOpen;
      updateActionSurfaceState({
        isMenuOpen: nextIsMenuOpen
      });
    };
    $[31] = updateActionSurfaceState;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const handleTriggerClick = t19;
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = event_3 => {
      if (event_3.key !== "Enter" && event_3.key !== " " && event_3.key !== "Spacebar") {
        return;
      }
      stopPropagation(event_3);
      shouldFocusFirstMenuItemRef.current = true;
    };
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const handleTriggerKeyDown = t20;
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = event_4 => {
      shouldFocusFirstMenuItemRef.current = false;
      shouldIgnoreNextPointerFocusRef.current = true;
      stopPropagation(event_4);
    };
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  const handleTriggerPointerStart = t21;
  let t22;
  if ($[35] !== closeMenu) {
    t22 = event_5 => {
      event_5.preventDefault();
      stopPropagation(event_5);
      closeMenu();
    };
    $[35] = closeMenu;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const dismissOverlayMenu = t22;
  let t23;
  if ($[37] !== updateActionSurfaceState) {
    t23 = () => {
      updateActionSurfaceState({
        isFocused: false
      });
    };
    $[37] = updateActionSurfaceState;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const handleTriggerBlur = t23;
  let t24;
  if ($[39] !== updateActionSurfaceState) {
    t24 = () => {
      if (shouldIgnoreNextPointerFocusRef.current) {
        shouldIgnoreNextPointerFocusRef.current = false;
        return;
      }
      updateActionSurfaceState({
        isFocused: true
      });
    };
    $[39] = updateActionSurfaceState;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  const handleTriggerFocus = t24;
  let t25;
  if ($[41] !== isOverlay || $[42] !== isPreviewToggleDisabled || $[43] !== previewToggle?.isLoading || $[44] !== previewToggle?.label || $[45] !== showPreviewToggle || $[46] !== toggleLinkPreviews) {
    t25 = showPreviewToggle ? <button ref={handlePreviewToggleButtonRef} type="button" className={`${isOverlay ? MENU_ITEM_CLASSES : RAIL_BUTTON_CLASSES} ${isPreviewToggleDisabled ? "tw-cursor-default tw-opacity-60" : ""}`} aria-label={previewToggle?.label} disabled={isPreviewToggleDisabled} onClick={toggleLinkPreviews} onPointerDown={stopPropagation} onMouseDown={stopPropagation} onTouchStart={stopPropagation}><PreviewToggleIcon isLoading={previewToggle?.isLoading ?? false} />{isOverlay && <span>{previewToggle?.label}</span>}</button> : null;
    $[41] = isOverlay;
    $[42] = isPreviewToggleDisabled;
    $[43] = previewToggle?.isLoading;
    $[44] = previewToggle?.label;
    $[45] = showPreviewToggle;
    $[46] = toggleLinkPreviews;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  const previewToggleButton = t25;
  const t26 = isOverlay ? MENU_ITEM_CLASSES : RAIL_BUTTON_CLASSES;
  let t27;
  if ($[48] !== copied || $[49] !== isOverlay) {
    t27 = isOverlay ? <DocumentDuplicateIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <svg className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-transition tw-duration-300 tw-ease-out" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke={copied ? "#27ae60" : "currentColor"} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" /></svg>;
    $[48] = copied;
    $[49] = isOverlay;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== isOverlay) {
    t28 = isOverlay && <span>Copy link</span>;
    $[51] = isOverlay;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== copyToClipboard || $[54] !== t26 || $[55] !== t27 || $[56] !== t28) {
    t29 = <button ref={handleCopyButtonRef} type="button" className={t26} aria-label="Copy link" onClick={copyToClipboard} onPointerDown={stopPropagation} onMouseDown={stopPropagation} onTouchStart={stopPropagation}>{t27}{t28}</button>;
    $[53] = copyToClipboard;
    $[54] = t26;
    $[55] = t27;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  const copyButton = t29;
  let t30;
  if ($[58] !== closeMenu || $[59] !== hideLink || $[60] !== href || $[61] !== isOverlay || $[62] !== relativeHref) {
    t30 = hideLink ? null : <Link ref={handleOpenLinkButtonRef} href={relativeHref ?? href} target={relativeHref ? undefined : "_blank"} className={isOverlay ? MENU_ITEM_CLASSES : RAIL_BUTTON_CLASSES} onClick={event_6 => {
      stopPropagation(event_6);
      closeMenu({
        restoreFocusToTrigger: event_6.detail === 0
      });
    }} onPointerDown={stopPropagation} onMouseDown={stopPropagation} onTouchStart={stopPropagation}>{isOverlay ? <ArrowTopRightOnSquareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <ExternalLinkIcon />}{isOverlay && <span>Open link</span>}</Link>;
    $[58] = closeMenu;
    $[59] = hideLink;
    $[60] = href;
    $[61] = isOverlay;
    $[62] = relativeHref;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const openLinkButton = t30;
  if (!isOverlay) {
    let t31;
    if ($[64] !== copyButton || $[65] !== openLinkButton || $[66] !== previewToggleButton) {
      t31 = <div className="tw-mt-1 tw-flex tw-h-full tw-flex-col tw-justify-start tw-gap-y-2">{previewToggleButton}{copyButton}{openLinkButton}</div>;
      $[64] = copyButton;
      $[65] = openLinkButton;
      $[66] = previewToggleButton;
      $[67] = t31;
    } else {
      t31 = $[67];
    }
    return t31;
  }
  let t31;
  if ($[68] !== dismissOverlayMenu || $[69] !== isMenuOpen) {
    t31 = isMenuOpen && <button type="button" tabIndex={-1} aria-label="Dismiss link actions" className="tw-absolute tw-inset-0 tw-z-10 tw-cursor-default tw-border-0 tw-bg-transparent tw-p-0" onClick={dismissOverlayMenu} onPointerDown={dismissOverlayMenu} onMouseDown={dismissOverlayMenu} onTouchStart={dismissOverlayMenu} />;
    $[68] = dismissOverlayMenu;
    $[69] = isMenuOpen;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const t32 = `tw-transition-opacity tw-duration-200 ${showPersistentOverlayTrigger || isMenuOpen ? "tw-pointer-events-auto tw-opacity-100" : "tw-pointer-events-none tw-opacity-0 group-focus-within/link-card:tw-pointer-events-auto group-focus-within/link-card:tw-opacity-100 desktop-hover:group-hover/link-card:tw-pointer-events-auto desktop-hover:group-hover/link-card:tw-opacity-100"}`;
  let t33;
  let t34;
  if ($[71] !== updateActionSurfaceState) {
    t33 = () => updateActionSurfaceState({
      isHovered: true
    });
    t34 = () => updateActionSurfaceState({
      isHovered: false
    });
    $[71] = updateActionSurfaceState;
    $[72] = t33;
    $[73] = t34;
  } else {
    t33 = $[72];
    t34 = $[73];
  }
  let t35;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <EllipsisHorizontalIcon className="tw-h-5 tw-w-5" />;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] !== handleTriggerBlur || $[76] !== handleTriggerClick || $[77] !== handleTriggerFocus || $[78] !== isMenuOpen || $[79] !== t33 || $[80] !== t34) {
    t36 = <button ref={buttonRef} type="button" className={OVERLAY_TRIGGER_BUTTON_CLASSES} aria-label="Link actions" aria-haspopup="true" aria-expanded={isMenuOpen} onClick={handleTriggerClick} onMouseEnter={t33} onMouseLeave={t34} onFocus={handleTriggerFocus} onBlur={handleTriggerBlur} onKeyDown={handleTriggerKeyDown} onPointerDown={handleTriggerPointerStart} onMouseDown={handleTriggerPointerStart} onTouchStart={handleTriggerPointerStart}>{t35}</button>;
    $[75] = handleTriggerBlur;
    $[76] = handleTriggerClick;
    $[77] = handleTriggerFocus;
    $[78] = isMenuOpen;
    $[79] = t33;
    $[80] = t34;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t32 || $[83] !== t36) {
    t37 = <div className={t32}>{t36}</div>;
    $[82] = t32;
    $[83] = t36;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  let t38;
  if ($[85] !== previewToggleButton) {
    t38 = previewToggleButton && <li className="tw-list-none">{previewToggleButton}</li>;
    $[85] = previewToggleButton;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== copyButton) {
    t39 = <li className="tw-list-none">{copyButton}</li>;
    $[87] = copyButton;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== hideLink || $[90] !== openLinkButton) {
    t40 = !hideLink && <li className="tw-list-none">{openLinkButton}</li>;
    $[89] = hideLink;
    $[90] = openLinkButton;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== handleMenuOpenChange || $[93] !== isMenuOpen || $[94] !== t38 || $[95] !== t39 || $[96] !== t40) {
    t41 = <CommonDropdownItemsDefaultWrapper isOpen={isMenuOpen} setOpen={handleMenuOpenChange} buttonRef={buttonRef}>{t38}{t39}{t40}</CommonDropdownItemsDefaultWrapper>;
    $[92] = handleMenuOpenChange;
    $[93] = isMenuOpen;
    $[94] = t38;
    $[95] = t39;
    $[96] = t40;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] !== t37 || $[99] !== t41) {
    t42 = <div className="tw-absolute tw-bottom-3 tw-right-3 tw-z-20">{t37}{t41}</div>;
    $[98] = t37;
    $[99] = t41;
    $[100] = t42;
  } else {
    t42 = $[100];
  }
  let t43;
  if ($[101] !== t31 || $[102] !== t42) {
    t43 = <>{t31}{t42}</>;
    $[101] = t31;
    $[102] = t42;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  return t43;
}
function _temp(element) {
  return Boolean(element) && !element?.hasAttribute("disabled");
}